import {Checkbox, ListItemText, TextField} from "@mui/material";
import * as React from "react";
import {useState} from "react";
import MenuItem from "@mui/material/MenuItem";
import {COLOR_IMPORTED, COLOR_PROCESSED} from "../constants/EnelxColors";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {STATE_REPORT_ACTIVE_FILTERS} from "../constants/EnelxRecoil";
import {ID_IMPORTED, ID_LOADING, ID_PROCESSED} from "../constants/EnelxStatuses";
import {
    ID_FILTER_REPORT_IMPORTEDBY,
    ID_FILTER_REPORT_IMPORTEDDATEFROM,
    ID_FILTER_REPORT_IMPORTEDDATETO,
    ID_FILTER_REPORT_STATUSES,
    ID_FILTER_SEARCH,
    reportsDefaultValues, ID_FILTER_REPORT_LEGAL_ENTITIES, ID_FILTER_REPORT_DEALERS
} from "../constants/EnelxFilters";
import {useReportRepository} from "../../../api/repositories/ReportRepository";
import * as Yup from "yup";
import AsyncAutocomplete from "../../../component/AsyncAutocomplete";
import AdvancedFilter from "../../../component/AdvancedFilter";
import ColouredDot from "../../../component/ColouredDot";
import {addFilter, getFilterValue, renderMultiSelect} from "../../../utils/FilterUtils";
import {asEndOfDay, asStartOfDay} from "../../../utils/FinanceDateUtils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

function ReportsFilter() {
    const {t} = useTranslation('finance', {keyPrefix: 'enelx'});
    const {t: tg} = useTranslation("translation");

    // Use Context
    const {
        globalData: {user},
    } = React.useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const {getCreators} = useReportRepository();

    const setFilters = useSetRecoilState(STATE_REPORT_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const [search, setSearch] = useState("");

    const schema = Yup.object().shape({
        creator: Yup.string(),
        importDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.importDateTo || (value <= context.parent.importDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        importDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.importDateFrom || (value >= context.parent.importDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        selectedStatuses: Yup.array(),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne')),
    });

    const defaultValues = {
        creator: getFilterValue(ID_FILTER_REPORT_IMPORTEDBY, filterDefs) || "",
        selectedStatuses: getFilterValue(ID_FILTER_REPORT_STATUSES, filterDefs) || [],
        importDateFrom: getFilterValue(ID_FILTER_REPORT_IMPORTEDDATEFROM, filterDefs) || null,
        importDateTo: getFilterValue(ID_FILTER_REPORT_IMPORTEDDATETO, filterDefs) || null,
        legalEntities:
            getFilterValue(ID_FILTER_REPORT_LEGAL_ENTITIES, filterDefs) || legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_REPORT_DEALERS, filterDefs) || [],
    };

    const renderCreatorField = (field: any) => (
        <AsyncAutocomplete
            id={"creator"}
            callback={it => getCreators(it).then(it => it.result)}
            value={field.value}
            onChange={field.onChange}
            renderInput={(params) => <TextField {...params}
                                                label={t('filters.reports.' + ID_FILTER_REPORT_IMPORTEDBY)}/>}
        />
    );

    const renderStatuses = (field: any) => (
        <TextField fullWidth
                   label={t('filters.reports.' + ID_FILTER_REPORT_STATUSES)}
                   select
                   SelectProps={{
                       multiple: true,
                       renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                   }}
                   value={field.value}
                   onChange={field.onChange}
        >
            {statuses.map((status) =>
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <ColouredDot color={status?.color}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            )}
        </TextField>
    )

    const renderImportDateFrom = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.reports.' + ID_FILTER_REPORT_IMPORTEDDATEFROM)}
                    views={['day']}
                    maxDate={values['importDateTo'] !== null ? values['importDateTo'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"importDateFrom"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )
    const renderImportDateTo = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.reports.' + ID_FILTER_REPORT_IMPORTEDDATETO)}
                    views={['day']}
                    minDate={values['importDateFrom'] !== null ? values['importDateFrom'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"importDateTo"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )

    const fields = {
        creator: renderCreatorField,
        importDateFrom: renderImportDateFrom,
        importDateTo: renderImportDateTo,
        selectedStatuses: renderStatuses,
        legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
                setValue('dealers', []);
                let dealers = user?.dealers
                    ?.filter(
                        (x: any) =>
                            value?.length > 0 &&
                            x.legalEntities.some((e: any) => value.includes(e.name)) &&
                            x.name.toLowerCase() !== "all"
                    )
                    .map((x: any) => {
                        return {
                            label: tg(`dealers.${x.name}`),
                            value: x.name,
                        };
                    });
                setDealers(dealers);
            }
        ),
        dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)
    };


    const statuses = [
        {
            value: ID_LOADING,
            label: t('statuses.' + ID_LOADING),
        },
        {
            value: ID_IMPORTED,
            label: t('statuses.' + ID_IMPORTED),
            color: COLOR_IMPORTED + '.transparent'
        },
        {
            value: ID_PROCESSED,
            label: t('statuses.' + ID_PROCESSED),
            color: COLOR_PROCESSED + '.transparent'
        },
    ];

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_REPORT_IMPORTEDBY, data.creator);
        addFilter(setFilters, ID_FILTER_REPORT_IMPORTEDDATEFROM, asStartOfDay(data.importDateFrom));
        addFilter(setFilters, ID_FILTER_REPORT_IMPORTEDDATETO, asEndOfDay(data.importDateTo));
        addFilter(setFilters, ID_FILTER_REPORT_STATUSES, data.selectedStatuses);
        addFilter(setFilters, ID_FILTER_REPORT_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_REPORT_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilters([]);
        setDealers(dealerOptions);
    }

    return (
        <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                        searchInput={search} setSearchInput={setSearch}
                        onSubmit={submit}
                        onReset={reset}
                        customDefaultValues={reportsDefaultValues}
        />
    )
}

export default ReportsFilter;